// ---------------------------------------------------------------------------
// Judge Factory store — State + Action types.
//
// `GraderNavState` is the slice managed by the reducer: selection,
// mode, tab, variant cursor, and the dialog open/close flags.
//
// `GraderState` is the full *public* state exposed by the provider —
// it overlays the judge collection (judges + load state) on top of the nav
// slice so consumers can read everything from one context.
// ---------------------------------------------------------------------------

import type { AppError } from '../api/errors.js';
import type { Grader } from '../hooks/useGraders/useGraders.interface.js';
import type { DetailViewTab } from '../pages/GraderPage/GraderDetailView/GraderDetailView.js';

export type GraderMode = 'view' | 'create';

/**
 * How the user chose to author a new judge:
 *   - `'grader'` — the full pipeline flow (description + golden examples).
 *   - `'systemPrompt'` — author a system prompt by hand; the grader is created
 *     directly with no golden dataset or provisioning.
 *   - `'numeric'` — author a numeric-comparison grader by hand (scale bounds +
 *     expected/actual score expressions); created directly like `systemPrompt`.
 */
export type GraderCreationMethod =
  | 'grader'
  | 'systemPrompt'
  | 'numeric'
  | 'latency';

export interface GraderNavState {
  /** Whether the page is in existing-judge view mode or the creation flow. */
  mode: GraderMode;
  /**
   * Which creation flow is active while `mode === 'create'`. Ignored in view
   * mode; retains its last value so the reducer stays a pure switch.
   */
  creationMethod: GraderCreationMethod;
  /** ID of the selected judge (always null while in create mode). */
  selectedId: string | null;
  /** Active tab inside the existing-judge editor. */
  editorTab: DetailViewTab;
  /** Judge pending delete confirmation; `null` while the dialog is closed. */
  judgeToDelete: Grader | null;
}

export interface GraderState extends GraderNavState {
  workspaceName: string;
  /** All graders in the current workspace. Updated on workspace-switch and
   *  on local optimistic patches (insert / patch / remove). */
  graders: Grader[];
  /**
   * Resolved grader for `selectedId`, computed in the provider so consumers
   * can destructure it without doing the lookup themselves. `null` when
   * nothing is selected or when the id can't be found in `graders` (e.g.
   * mid-delete). The id remains the source of truth in the reducer.
   */
  selectedGrader: Grader | null;
  /** True while the initial fetch of the workspace's graders is in flight. */
  gradersLoading: boolean;
  /** Error from the initial grader fetch, if any. */
  gradersError: AppError | null;
}

export type GraderAction =
  /** Select a judge — also resets editor tab. */
  | { type: 'SELECT_JUDGE'; id: string | null }
  | { type: 'SET_EDITOR_TAB'; tab: DetailViewTab }
  /** Enter create flow with the chosen method — clears selection and resets editor tab. */
  | { type: 'ENTER_CREATE_MODE'; method: GraderCreationMethod }
  /** Exit create flow — return to view mode on the given judge. */
  | { type: 'EXIT_CREATE_MODE'; selectedId: string | null }
  | { type: 'OPEN_DELETE_DIALOG'; judge: Grader }
  | { type: 'CLOSE_DELETE_DIALOG' };

export const INITIAL_JUDGE_FACTORY_NAV_STATE: GraderNavState = {
  mode: 'view',
  creationMethod: 'grader',
  selectedId: null,
  editorTab: 'overview',
  judgeToDelete: null,
};
